import React, {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useState,
} from "react";

import { getMatchingKeys } from "../../utils/objects";
import { isStringEmptyOrZero } from "../../utils/validations";

type FocusableElement = HTMLInputElement | HTMLTextAreaElement;

type ActionableRef = RefObject<HTMLElement | FocusableElement>;

type ContextProps = {
  registerElement: (key: string, ref: ActionableRef) => void;
  unregisterElement: (key: string) => void;
  focusOn: (id: string) => void;
  focusOnFirstEmpty: (regexp?: RegExp) => void;
  scrollTo: (refKey: string) => void;
};

export const UIActionContext = createContext<ContextProps | undefined>(
  undefined
);

type ProviderProps = {
  children: ReactNode;
};

export const UIActionProvider: FC<ProviderProps> = ({ children }) => {
  const [refMap, setRefMap] = useState<
    Record<string, ActionableRef | undefined>
  >({});

  const registerElement = useCallback((key: string, ref: ActionableRef) => {
    setRefMap((prev) => ({ ...prev, [key]: ref }));
  }, []);

  const unregisterElement = useCallback((key: string) => {
    setRefMap((prev) => ({ ...prev, [key]: undefined }));
  }, []);

  // Start of definition of UI Actions
  const focusOn = useCallback(
    (key: string) => {
      if (refMap) {
        refMap[key]?.current?.focus();
      }
    },
    [refMap]
  );

  const focusOnFirstEmpty = useCallback(
    (regexp?: RegExp) => {
      const keys = regexp
        ? getMatchingKeys(refMap, regexp)
        : Object.keys(refMap);

      keys.some((key) => {
        if (refMap[key]?.current) {
          const inputRef = refMap[key]?.current as FocusableElement;

          if (inputRef && isStringEmptyOrZero(inputRef.value)) {
            inputRef.focus();
            return true;
          }
        }

        return false;
      });
    },
    [refMap]
  );

  const scrollTo = useCallback(
    (key: string) => {
      if (refMap) {
        refMap[key]?.current?.scrollIntoView({ behavior: "smooth" });
      }
    },
    [refMap]
  );
  // End of definition of UI Actions

  return (
    <UIActionContext.Provider
      value={{
        registerElement,
        unregisterElement,
        focusOn,
        focusOnFirstEmpty,
        scrollTo,
      }}
    >
      {children}
    </UIActionContext.Provider>
  );
};
